/**
 * ChatMessageInput
 *
 * Chat-specific composer input that mirrors the canonical `MessageInput`
 * (`presentation/components/common/MessageInput.tsx`) shape — uncontrolled
 * draft, imperative `clear()` handle, auto-resizing textarea, animated
 * send button — and renders the add-media + gift buttons inline between the
 * textarea and the send button (matching Flutter `chat_screen.dart:3592-3742`
 * which places them between the `TextField` and the send icon).
 *
 * Modeled after `CommentInput.tsx` (the existing in-tree pattern that already
 * stacks an inline action button next to the send button). Kept feature-local
 * because the right-side icon row is chat-specific; `MessageInput` continues
 * to serve the share-sheet / mock-chat call-sites unchanged.
 *
 * Deviations from Flutter:
 *  - Add-media + gift + send buttons are ALL always visible. Flutter hides the
 *    send button when the field is unfocused and only shows the add-media +
 *    gift buttons in that state (`Visibility(visible: !_focusNode.hasFocus,
 *    ...)` at lines 3621-3683 vs `Visibility(visible: _focusNode.hasFocus,
 *    ...)` at line 3686). The web keeps all three right-side icons mounted
 *    regardless of focus — a deliberate web UI choice — so there is no
 *    focus gate and no `trailingActions` slot; the add-media + gift buttons
 *    render directly here. The gift tap (send-gift sheet, chat variant), its
 *    `peer-pending` qa-status token, and the gift aria-label are threaded in as
 *    props so `SendGiftSheet`/`useLocale` stay in `ChatComposer`.
 */

import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { AddMediaButton } from '@/presentation/components/common/controls/AddMediaButton';
import { InputText, type InputTextElement } from '@/presentation/components/common/InputText';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ChatMessageInputProps {
  /** Optional initial draft text. Defaults to `''`. Not re-read after mount. */
  defaultValue?: string;
  /**
   * Called with the current draft when the user presses Enter (without Shift)
   * or clicks the send button. The draft is NOT cleared automatically — the
   * caller decides via the imperative `clear()` handle.
   */
  onSend: (text: string) => void;
  disabled: boolean;
  loading: boolean;
  placeholder?: string;
  /** Tap handler for the add-media button. Stubs to "not implemented yet" in the caller. */
  onMediaTap: () => void;
  /** Tap handler for the gift button. Opens the send-gift sheet (chat variant) in the caller; no-ops while the peer is unresolved. */
  onGiftTap: () => void;
  /**
   * Coarse lifecycle token for the gift button's `data-qa-status`, derived in
   * the caller from the same `peerUser == null` check that no-ops `onGiftTap`:
   * `'peer-pending'` while the peer is unresolved (cold deep-link window),
   * `'ready'` once it resolves. Test-only; l10n-exempt.
   */
  giftStatus: string;
  /** Accessible label for the gift button (l10n lives in the caller). */
  giftAriaLabel: string;
  /**
   * Dims + natively disables the gift button while the peer is unresolved
   * (`peer-pending`). Derived in the caller from the SAME `peerUser == null`
   * source as `giftStatus` and the `onGiftTap` no-op gate, so the visual
   * affordance, the QA status token, and the real gate never disagree. The
   * `onClick` wiring and `data-qa-status` token are left untouched — a native
   * `disabled` button just no-ops the click, matching the existing handler
   * guard. Optional; defaults to `false`. */
  giftDisabled?: boolean;
}

export interface ChatMessageInputHandle {
  /** Reset the draft to the empty string and collapse the auto-resized textarea. */
  clear: () => void;
}

export const ChatMessageInput = forwardRef<
  ChatMessageInputHandle,
  ChatMessageInputProps
>(function ChatMessageInput(
  {
    defaultValue = '',
    onSend,
    disabled,
    loading,
    placeholder,
    onMediaTap,
    onGiftTap,
    giftStatus,
    giftAriaLabel,
    giftDisabled = false,
  },
  ref,
) {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const [value, setValue] = useState<string>(defaultValue);
  const [isInputingText, setIsInputingText] = useState<boolean>(
    defaultValue.length > 0,
  );
  const inputRef = useRef<InputTextElement | null>(null);

  const resetTextareaHeight = () => {
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  useImperativeHandle(
    ref,
    () => ({
      clear: () => {
        setValue('');
        setIsInputingText(false);
        resetTextareaHeight();
      },
    }),
    [],
  );

  const handleTextChanged = (newValue: string) => {
    setValue(newValue);

    // Mirrors `MessageInput`'s `_textChanged` parity (Flutter line 311).
    if (!isInputingText && newValue.length > 0) {
      setIsInputingText(true);
    } else if (newValue.length === 0) {
      setIsInputingText(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<InputTextElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled && !loading) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    onSend(value);
    resetTextareaHeight();
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        flex: 1,
        minWidth: 0,
        gap: responsive.gap.sm,
      }}
    >
      <InputText
        inputRef={inputRef}
        value={value}
        onChange={handleTextChanged}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        backgroundColor={theme.background}
        multiline
        autoResize
        lineHeight={1.4}
        maxHeight="6.2em"
        style={{ flex: 1 }}
        qa={{ id: 'chat-message-input' }}
      />

      {/* Right-side icon row — add-media, gift, send. Always visible (web
          deviation from Flutter's focus-gated `Visibility`, see file header).
          Order matches Flutter `Row(children: [TextField, AddMedia, Gift,
          Send])` at `chat_screen.dart:3592-3742`. Evenly spaced at
          `responsive.gap.xl`; the textarea-to-icons gap stays on the outer
          row at `responsive.gap.sm`. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.gap.xl,
          flexShrink: 0,
        }}
      >
        <AddMediaButton
          backgroundColor={theme.background}
          iconBackgroundColor={theme.secondary}
          buttonSize={responsive.button.size.xs}
          iconSize={responsive.icon.small}
          onTap={onMediaTap}
          qa={{ id: 'chat-composer-attach' }}
        />

        <button
          type="button"
          {...qaAttr({ id: 'chat-composer-gift', status: giftStatus })}
          onClick={onGiftTap}
          disabled={giftDisabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: giftDisabled ? 'not-allowed' : 'pointer',
            opacity: giftDisabled ? responsive.opacity.disabled : 1,
            padding: 0,
            flexShrink: 0,
          }}
          aria-label={giftAriaLabel}
        >
          <AssetIcon
            path="/assets/images/gift.png"
            width={responsive.icon.medium}
            height={responsive.icon.medium}
            color={giftDisabled ? theme.disabled : theme.text.primary}
          />
        </button>

        <button
          {...qaAttr({ id: 'chat-composer-send' })}
          onClick={handleSend}
          disabled={disabled || loading}
          style={{
            borderRadius: responsive.borderRadius.round,
            border: 'none',
            background: 'transparent',
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
            transform: isInputingText ? 'rotate(-45deg)' : 'rotate(0deg)',
            transition:
              'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), background-color 0.2s',
          }}
        >
          {loading ? (
            <div
              style={{
                transform: isInputingText ? 'rotate(45deg)' : 'rotate(0deg)',
              }}
            >
              <ExpauseLoadingIndicator />
            </div>
          ) : (
            <AssetIcon
              path="/assets/images/send.png"
              width={responsive.button.size.xxs}
              height={responsive.button.size.xxs}
              color={
                disabled
                  ? theme.disabled
                  : isInputingText
                    ? theme.exclusive
                    : theme.icon.primary
              }
              transition="background-color 0.2s"
            />
          )}
        </button>
      </div>
    </div>
  );
});
